import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Link,
  SimpleGrid,
  Text
} from "@chakra-ui/react"
import Footer from "../components/Footer"

const steps = [
  {
    label: "Source",
    description:
      "We source high potential properties on and off market for you or analyse what you own automatically."
  },
  {
    label: "Analyse",
    description:
      "AI spots opportunities based on strong energy upgrade ROI and matches funding opportunities."
  },
  {
    label: "Activate",
    description:
      "We recommend installers, monitor portfolio performance and report impact."
  }
]

const benefits = [
  {
    title: "Turn Energy Upgrades into Predictable Returns",
    body: "GreenFlip gives you instant, portfolio-wide insight into where energy upgrades deliver the strongest financial returns."
  },
  {
    title: "Maximise Investment Returns",
    body: "Identify and prioritise the highest ROI opportunities across your portfolio. Model uplift in asset value, rental income, and long-term yield before you invest."
  },
  {
    title: "Stay Ahead of Regulation",
    body: "Future-proof your assets against tightening EPC requirements. De-risk stranded assets and ensure full compliance with evolving standards."
  },
  {
    title: "Increase Rental Performance",
    body: "Deliver warmer, healthier homes that tenants stay in longer. Reduce voids and offer competitive rents by lowering or eliminating energy bills."
  },
  {
    title: "Reduce Operational Costs",
    body: "Lower maintenance and operational costs with modern, efficient systems. Move away from high-maintenance and risky legacy heating."
  },
  {
    title: "Make Smarter Portfolio Decisions",
    body: "Use retrofit intelligence to inform acquisitions, disposals, and asset management. Underwrite deals with energy performance and future compliance built in."
  }
]

const InstitutionalInvestors: React.FC = () => (
  <Box>
    {/* Hero */}
    <Box bg="brand.700">
      <Container maxW="container.md" py={24} textAlign="center">
        <Heading as="h1" size="5xl" color="white" mb={6}>
          Institutional Investors
        </Heading>
        <Text fontSize="xl" color="whiteAlpha.900" maxW={700} m="auto">
          Turn residential energy upgrades into predictable investments using
          AI-driven energy and financial modelling.
        </Text>
      </Container>
    </Box>

    {/* How it works */}
    <Container maxW="container.md" py={20}>
      <HStack gap={6} align="stretch" flexWrap={["wrap", "nowrap"]}>
        {steps.map((step, i) => (
          <Box
            key={step.label}
            flex={1}
            minW={["100%", "0"]}
            bg="gray.50"
            p={8}
            borderRadius="2xl"
            borderTop="4px solid"
            borderColor="brand.600"
          >
            <Text
              fontSize="xs"
              fontWeight="bold"
              color="brand.600"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={2}
            >
              {String(i + 1).padStart(2, "0")}
            </Text>
            <Heading as="h3" size="lg" mb={3} color="green.800">
              {step.label}
            </Heading>
            <Text color="gray.600">{step.description}</Text>
          </Box>
        ))}
      </HStack>
    </Container>

    {/* Benefits */}
    <Box bg="gray.50">
      <Container maxW="container.md" py={20}>
        <Heading
          as="h2"
          size="3xl"
          mb={12}
          textAlign="center"
          color="green.800"
        >
          Key Benefits for Property Investors
        </Heading>
        <SimpleGrid columns={[1, 2]} gap={6}>
          {benefits.map((b) => (
            <Box key={b.title} bg="white" p={6} borderRadius="xl" shadow="sm">
              <Heading as="h3" size="md" mb={3} color="green.800">
                {b.title}
              </Heading>
              <Text color="gray.600" fontSize="sm" lineHeight="tall">
                {b.body}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>

    {/* CTA */}
    <Box bg="brand.700">
      <Container maxW="container.md" py={20} textAlign="center">
        <Heading as="h2" size="3xl" color="white" mb={6}>
          Ready to analyse your portfolio?
        </Heading>
        <Text color="whiteAlpha.900" fontSize="lg" mb={8} maxW={600} m="auto">
          Reach out to our team to analyse ROI, compliance, and asset value
          uplift opportunities across your portfolio.
        </Text>
        <Link href="/contact">
          <Button
            mt={6}
            bg="white"
            color="brand.700"
            size="lg"
            _hover={{ bg: "gray.100" }}
          >
            Get in touch
          </Button>
        </Link>
      </Container>
    </Box>

    <Footer />
  </Box>
)

export default InstitutionalInvestors
